import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Mail, X, Save } from "lucide-react";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const CATEGORIES = [
  { id: "first_contact", label: "First Contact" },
  { id: "follow_up", label: "Follow-up" },
  { id: "meeting_request", label: "Meeting Request" },
  { id: "re_engagement", label: "Re-engagement" },
];

export default function Templates({ user }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    body: "",
    category: "first_contact",
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API}/templates`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        subject: template.subject,
        body: template.body,
        category: template.category,
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        name: "",
        subject: "",
        body: "",
        category: "first_contact",
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.subject || !formData.body) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const url = editingTemplate
        ? `${API}/templates/${editingTemplate.template_id}`
        : `${API}/templates`;
      const method = editingTemplate ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        if (editingTemplate) {
          setTemplates((prev) =>
            prev.map((t) =>
              t.template_id === editingTemplate.template_id ? result : t
            )
          );
          toast.success("Template updated");
        } else {
          setTemplates((prev) => [result, ...prev]);
          toast.success("Template created");
        }
        setShowModal(false);
      } else {
        toast.error("Failed to save template");
      }
    } catch (error) {
      toast.error("Failed to save template");
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this template?")) return;

    try {
      const response = await fetch(`${API}/templates/${templateId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        setTemplates((prev) => prev.filter((t) => t.template_id !== templateId));
        toast.success("Template deleted");
      } else {
        toast.error("Failed to delete template");
      }
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const getCategoryLabel = (categoryId) => {
    return CATEGORIES.find((c) => c.id === categoryId)?.label || categoryId;
  };

  const getCategoryColor = (categoryId) => {
    switch (categoryId) {
      case "first_contact":
        return "bg-blue-100 text-blue-700";
      case "follow_up":
        return "bg-amber-100 text-amber-700";
      case "meeting_request":
        return "bg-purple-100 text-purple-700";
      case "re_engagement":
        return "bg-emerald-100 text-emerald-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
            Email Templates
          </h1>
          <p className="text-slate-500 mt-1">
            Create and manage email templates for your outreach
          </p>
        </div>
        <Button
          className="bg-blue-600 hover:bg-blue-700"
          onClick={() => handleOpenModal()}
          data-testid="create-template-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Create Template
        </Button>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templates.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="text-center py-12">
              <Mail className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">
                No templates yet. Create your first email template to get started.
              </p>
              <Button
                className="mt-4 bg-blue-600 hover:bg-blue-700"
                onClick={() => handleOpenModal()}
              >
                Create Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          templates.map((template) => (
            <Card
              key={template.template_id}
              className="hover:shadow-md transition-shadow"
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    <Badge
                      variant="secondary"
                      className={`mt-2 ${getCategoryColor(template.category)}`}
                    >
                      {getCategoryLabel(template.category)}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenModal(template)}
                      data-testid={`edit-template-${template.template_id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDelete(template.template_id)}
                      data-testid={`delete-template-${template.template_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm font-medium text-slate-700 mb-2">
                  Subject: {template.subject}
                </p>
                <div
                  className="text-sm text-slate-500 line-clamp-3"
                  dangerouslySetInnerHTML={{
                    __html: template.body.substring(0, 200) + "...",
                  }}
                />
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Template Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Edit Template" : "Create Template"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Template Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Q1 Follow-up"
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) =>
                    setFormData({ ...formData, category: v })
                  }
                >
                  <SelectTrigger data-testid="template-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Subject Line</Label>
              <Input
                value={formData.subject}
                onChange={(e) =>
                  setFormData({ ...formData, subject: e.target.value })
                }
                placeholder="Use {{company_name}}, {{contact_name}} as placeholders"
                data-testid="template-subject-input"
              />
            </div>
            <div>
              <Label>Email Body (HTML supported)</Label>
              <Textarea
                value={formData.body}
                onChange={(e) =>
                  setFormData({ ...formData, body: e.target.value })
                }
                placeholder="Write your email template. Use {{company_name}}, {{contact_name}}, {{sender_name}} as placeholders."
                rows={12}
                data-testid="template-body-input"
              />
              <p className="text-xs text-slate-400 mt-1">
                Available placeholders: {"{{company_name}}"}, {"{{contact_name}}"}, {"{{sender_name}}"}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleSave}
              data-testid="save-template-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {editingTemplate ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
